import { Component } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { get as getSettings } from "../../../actions/settings";
import { deepEqual } from "../../../utils/object";

export class SettingListSelect extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: props.items ?? [],              // local copy (subclass may load)
      index: this.initIndexFromProps(props), // selected index
    };
  }

  // ---------- helpers ----------
  // Normalize any list item into a react-select option {value,label}
  normalizeOption = (v) => {
    if (v && typeof v === "object" && typeof v.value === "string" && typeof v.label === "string") {
      return v; // already shaped
    }
    // string (or other) -> coerce
    const s = v == null ? "" : String(v);
    return { value: s, label: s };
  };

  initIndexFromProps = (props) => {
    const src = props.items ?? this.state?.items ?? [];
    const opts = (src ?? []).map(this.normalizeOption);

    if (props.indexed && Number.isInteger(props.index)) {
      return clampIndex(props.index, opts.length);
    }

    if (props.value != null) {
      const wanted = String(props.value);
      const i = opts.findIndex((o) => String(o.value) === wanted);
      return i >= 0 ? i : null;
    }

    return null;
  };

  get options() {
    const src = this.state.items.length ? this.state.items : (this.props.items ?? []);
    return (src ?? []).map(this.normalizeOption);
  }

  get selectedOption() {
    const opts = this.options;
    const i = this.state.index;
    return i != null && i >= 0 && i < opts.length ? opts[i] : null;
  }

  // ---------- lifecycle ----------
  componentDidUpdate(prevProps) {
    // If parent changes the items array (by reference or contents), or value/index, recompute index.
    const itemsChanged = !deepEqual(this.props.items, prevProps.items);
    const valueChanged = this.props.value !== prevProps.value;
    const indexChanged = this.props.index !== prevProps.index || this.props.indexed !== prevProps.indexed;

    if (itemsChanged) {
      // replace local items when provided via props
      this.setState((s, p) => ({
        items: p.items ?? [],
        index: this.initIndexFromProps(p),
      }));
      return;
    }

    if (valueChanged || indexChanged) {
      this.setState({ index: this.initIndexFromProps(this.props) });
    }
  }

  // ---------- events ----------
  onChange = (optionOrNull) => {
    const selectedOption = optionOrNull ?? null;
    const opts = this.options;

    const newIndex = selectedOption
      ? opts.findIndex((o) => String(o.value) === String(selectedOption.value))
      : null;

    if (newIndex !== this.state.index) {
      this.setState({ index: newIndex }, this.doPostChange);
    }
  };

  doPostChange = () => {
    const opt = this.selectedOption;
    if (this.props.onChange) {
      this.props.onChange({
        target: {
          name: this.props.name,
          id: this.props.id,
          value: opt ? opt.value : null,
          index: this.state.index,
        },
      });
    }
  };

  render() {
    return (
      <Select
        id={this.props.id}
        value={this.selectedOption}                // <-- a single option (or null)
        className={this.props.className}
        name={this.props.name}
        onChange={this.onChange}
        classNamePrefix="react-select"
        placeholder=""
        options={this.options}
        isClearable={true}
      />
    );
  }
}

// Utility: keep an index in range
function clampIndex(idx, len) {
  if (!Number.isInteger(idx) || len <= 0) return null;
  if (idx < 0) return 0;
  if (idx >= len) return len - 1;
  return idx;
}

// --------- subclass that loads items via Redux action ----------
export class DataSettingListSelect extends SettingListSelect {
  async componentDidMount() {
    await this.loadData();
  }

  loadData = async () => {
    const { setting, category } = this.props;
    const item = await this.props.getSettings({ category, name: setting });

    let parsed;
    try {
      parsed = JSON.parse(item?.value);
    } catch {
      parsed = [];
    }
    const values = Array.isArray(parsed) ? parsed : [];

    // Detect shape (strings vs objects with value/label)
    const first = values.find(v => v != null);
    const isObjectList =
      first &&
      typeof first === "object" &&
      typeof first.value === "string" &&
      typeof first.label === "string";

    // Keep raw items (either strings or {value,label}); base class will normalize in options()
    const items = isObjectList ? values : values.map(v => (v == null ? "" : String(v)));

    // After items load, recompute index from props against the new list
    this.setState(
      { items },
      () => this.setState({ index: this.initIndexFromProps(this.props) })
    );
  };
}

const mapStateToProps = (state) => ({ auth: state.auth });
export const ConnectedSettingList = connect(mapStateToProps, { getSettings })(DataSettingListSelect);
